import {
  collection,
  deleteDoc,
  doc,
  limit as firestoreLimit,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import type { ActivityAggregation, DailyActivity } from '../../types/gamification';
import type { Keyboard } from '../../types/keyboard';
import type { LevelCompletion } from '../../types/levelCompletion';
import { LevelScore, UserProfile, UserProgress } from '../../types/progress';
import { IDatabaseService } from './IDatabaseService';

export class FirestoreService implements IDatabaseService {
  // Collection names
  private readonly USERS_COLLECTION = 'users';
  private readonly PROGRESS_COLLECTION = 'userProgress';
  private readonly LEVEL_HISTORY_COLLECTION = 'levelHistory';
  private readonly GLOBAL_CONFIG_COLLECTION = 'globalConfig';

  // User Profile Operations
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          ...data,
          createdAt: data.createdAt.toDate(),
          lastLoginAt: data.lastLoginAt.toDate(),
        } as UserProfile;
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  }

  async createUserProfile(profile: UserProfile): Promise<void> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, profile.uid);
      await setDoc(docRef, {
        ...profile,
        createdAt: Timestamp.fromDate(profile.createdAt),
        lastLoginAt: Timestamp.fromDate(profile.lastLoginAt),
      });
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId);
      const updateData: Record<string, unknown> = {};
      
      // Only add defined fields to avoid Firestore 400 errors
      Object.keys(updates).forEach(key => {
        const value = (updates as any)[key];
        if (value !== undefined) {
          updateData[key] = value;
        }
      });
      
      // Convert Date objects to Timestamps
      if (updates.lastLoginAt && updateData.lastLoginAt) {
        updateData.lastLoginAt = Timestamp.fromDate(updates.lastLoginAt);
      }
      
      if (Object.keys(updateData).length === 0) {
        console.warn('No valid fields to update in user profile');
        return;
      }
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // User Progress Operations
  async getUserProgress(
    userId: string,
    language: string,
    keyboardId: string
  ): Promise<UserProgress | null> {
    try {
      const progressId = `${userId}_${language}_${keyboardId}`;
      const docRef = doc(db, this.PROGRESS_COLLECTION, progressId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          ...data,
          levelScores: data.levelScores?.map((score: {completedAt: Timestamp}) => ({
            ...score,
            completedAt: score.completedAt.toDate(),
          })) || [],
        } as UserProgress;
      }
      return null;
    } catch (error) {
      console.error('Error getting user progress:', error);
      throw error;
    }
  }

  async createUserProgress(progress: UserProgress): Promise<void> {
    try {
      const progressId = `${progress.userId}_${progress.language}_${progress.keyboardId}`;
      const docRef = doc(db, this.PROGRESS_COLLECTION, progressId);
      
      await setDoc(docRef, {
        ...progress,
        levelScores: progress.levelScores.map(score => ({
          ...score,
          completedAt: Timestamp.fromDate(score.completedAt),
        })),
      });
    } catch (error) {
      console.error('Error creating user progress:', error);
      throw error;
    }
  }

  async updateUserProgress(
    userId: string,
    language: string,
    keyboardId: string,
    updates: Partial<UserProgress>
  ): Promise<void> {
    try {
      const progressId = `${userId}_${language}_${keyboardId}`;
      const docRef = doc(db, this.PROGRESS_COLLECTION, progressId);
      
      const updateData: Record<string, unknown> = { ...updates };
      
      // Convert Date objects in levelScores
      if (updates.levelScores) {
        updateData.levelScores = updates.levelScores.map(score => ({
          ...score,
          completedAt: Timestamp.fromDate(score.completedAt),
        }));
      }
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating user progress:', error);
      throw error;
    }
  }

  async getAllUserProgress(userId: string): Promise<UserProgress[]> {
    try {
      const q = query(
        collection(db, this.PROGRESS_COLLECTION),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      const progressList: UserProgress[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        progressList.push({
          ...data,
          levelScores: data.levelScores?.map((score: {completedAt: Timestamp}) => ({
            ...score,
            completedAt: score.completedAt.toDate(),
          })) || [],
        } as UserProgress);
      });
      
      return progressList;
    } catch (error) {
      console.error('Error getting all user progress:', error);
      throw error;
    }
  }

  // Level History Operations
  async saveLevelScore(userId: string, levelScore: LevelScore): Promise<void> {
    try {
      const historyRef = collection(
        db,
        this.LEVEL_HISTORY_COLLECTION,
        userId,
        'levels'
      );
      
      const levelId = `${levelScore.levelId}_${Date.now()}`;
      const docRef = doc(historyRef, levelId);
      
      await setDoc(docRef, {
        ...levelScore,
        completedAt: Timestamp.fromDate(levelScore.completedAt),
      });
    } catch (error) {
      console.error('Error saving level score:', error);
      throw error;
    }
  }

  async getLevelHistory(userId: string, limit: number = 50): Promise<LevelScore[]> {
    try {
      const historyRef = collection(
        db,
        this.LEVEL_HISTORY_COLLECTION,
        userId,
        'levels'
      );
      
      const q = query(
        historyRef,
        orderBy('completedAt', 'desc'),
        firestoreLimit(limit)
      );
      
      const querySnapshot = await getDocs(q);
      const history: LevelScore[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        history.push({
          ...data,
          completedAt: data.completedAt.toDate(),
        } as LevelScore);
      });
      
      return history;
    } catch (error) {
      console.error('Error getting level history:', error);
      throw error;
    }
  }

  /**
   * Save level completion (new architecture)
   */
  async saveLevelCompletion(userId: string, completion: LevelCompletion): Promise<void> {
    try {
      const completionRef = collection(
        db,
        this.USERS_COLLECTION,
        userId,
        'levelCompletions'
      );
      
      const docRef = doc(completionRef, completion.id);
      
      // Convert Date to Timestamp
      await setDoc(docRef, {
        ...completion,
        timestamp: Timestamp.fromDate(completion.timestamp),
      });
      
      console.log(`☁️ Saved level completion to Firestore: ${completion.id}`);
    } catch (error) {
      console.error('Error saving level completion:', error);
      throw error;
    }
  }

  /**
   * Query level completions with optional filters (bidirectional sync support)
   */
  async getLevelCompletions(
    userId: string,
    filters?: { courseId?: string; keyboardId?: string; levelId?: string; }
  ): Promise<LevelCompletion[]> {
    try {
      const completionsRef = collection(
        db,
        this.USERS_COLLECTION,
        userId,
        'levelCompletions'
      );
      
      let q = query(completionsRef);
      
      // Apply filters
      if (filters?.courseId) {
        q = query(q, where('courseId', '==', filters.courseId));
      }
      if (filters?.keyboardId) {
        q = query(q, where('keyboardId', '==', filters.keyboardId));
      }
      if (filters?.levelId) {
        q = query(q, where('levelId', '==', filters.levelId));
      }
      
      const snapshot = await getDocs(q);
      
      const completions: LevelCompletion[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          timestamp: data.timestamp?.toDate() || new Date(),
        } as LevelCompletion;
      });
      
      console.log(`📥 Fetched ${completions.length} level completions from Firestore`, filters);
      return completions;
    } catch (error) {
      console.error('Error fetching level completions from Firestore:', error);
      throw error;
    }
  }

  // Utility
  async deleteUserData(userId: string): Promise<void> {
    try {
      // Delete user profile
      await deleteDoc(doc(db, this.USERS_COLLECTION, userId));
      
      // Delete all user progress
      const progressQuery = query(
        collection(db, this.PROGRESS_COLLECTION),
        where('userId', '==', userId)
      );
      const progressSnapshot = await getDocs(progressQuery);
      
      const deletePromises = progressSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Note: Level history deletion would require recursive delete
      // Consider using Firebase Functions for this
    } catch (error) {
      console.error('Error deleting user data:', error);
      throw error;
    }
  }

  /**
   * Check if user data exists in Firestore
   * Used to detect if data was deleted by admin
   */
  async checkUserDataExists(userId: string): Promise<{
    profileExists: boolean;
    hasTrainingSessions: boolean;
    hasKeyboards: boolean;
    isOffline: boolean;
  }> {
    try {
      // Check if profile exists
      const profileDoc = await getDoc(doc(db, this.USERS_COLLECTION, userId));
      const profileExists = profileDoc.exists();

      // Check if training sessions exist (old format)
      const sessionsRef = collection(db, this.USERS_COLLECTION, userId, 'training_sessions');
      const sessionsSnapshot = await getDocs(query(sessionsRef, firestoreLimit(1)));
      const hasOldFormatSessions = !sessionsSnapshot.empty;

      // Check if aggregations exist (new format - more reliable indicator of progress)
      const aggregationsRef = collection(db, this.USERS_COLLECTION, userId, 'aggregations');
      const aggregationsSnapshot = await getDocs(query(aggregationsRef, firestoreLimit(1)));
      const hasAggregations = !aggregationsSnapshot.empty;

      // Check if level completions exist (another reliable indicator)
      const levelCompletionsRef = collection(db, this.USERS_COLLECTION, userId, 'levelCompletions');
      const levelCompletionsSnapshot = await getDocs(query(levelCompletionsRef, firestoreLimit(1)));
      const hasLevelCompletions = !levelCompletionsSnapshot.empty;

      // User has training sessions/progress if ANY of these exist
      const hasTrainingSessions = hasOldFormatSessions || hasAggregations || hasLevelCompletions;

      // Check if keyboards exist
      const keyboardsRef = collection(db, this.USERS_COLLECTION, userId, 'keyboards');
      const keyboardsSnapshot = await getDocs(query(keyboardsRef, firestoreLimit(1)));
      const hasKeyboards = !keyboardsSnapshot.empty;

      console.log(`  📊 checkUserDataExists for ${userId}: profile=${profileExists}, sessions=${hasTrainingSessions} (old=${hasOldFormatSessions}, agg=${hasAggregations}, levels=${hasLevelCompletions}), keyboards=${hasKeyboards}`);

      return { profileExists, hasTrainingSessions, hasKeyboards, isOffline: false };
    } catch (error) {
      console.error('Error checking user data existence:', error);
      
      // Check if the error is due to being offline
      const errorObj = error as { message?: string; code?: string };
      const isOfflineError = errorObj?.message?.includes('client is offline') || 
                             errorObj?.code === 'unavailable' ||
                             errorObj?.code === 'failed-precondition';
      
      if (isOfflineError) {
        console.warn('  🔌 Client is offline, skipping data existence check');
        return { profileExists: false, hasTrainingSessions: false, hasKeyboards: false, isOffline: true };
      }
      
      // For other errors, return false (data doesn't exist)
      return { profileExists: false, hasTrainingSessions: false, hasKeyboards: false, isOffline: false };
    }
  }

  /**
   * Get all users (admin only)
   * Returns basic user info for admin management
   */
  async getAllUsers(): Promise<Array<{ userId: string; displayName?: string; email?: string; createdAt?: Date }>> {
    try {
      const usersRef = collection(db, this.USERS_COLLECTION);
      const querySnapshot = await getDocs(usersRef);
      
      const users: Array<{ userId: string; displayName?: string; email?: string; createdAt?: Date }> = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          userId: doc.id,
          displayName: data.displayName,
          email: data.email,
          createdAt: data.createdAt?.toDate?.() || undefined,
        });
      });
      
      console.log(`👥 Loaded ${users.length} users from Firestore`);
      return users;
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        console.error('❌ Permission denied loading users. Make sure:', error);
        console.error('  1. Firestore rules are deployed (firebase deploy --only firestore:rules)');
        console.error('  2. Your user document has isAdmin: true');
        console.error('  3. You are logged in with an admin account');
        throw new Error('Permission denied: Admin access required. Check console for details.');
      }
      console.error('Error getting all users:', error);
      throw error;
    }
  }

  /**
   * Hard delete specific categories of user data from Firestore (admin only)
   * DANGER: This permanently deletes selected user data
   */
  async hardDeleteUserData(
    userId: string,
    options: {
      deleteProgress?: boolean;      // Training sessions, aggregations, level completions, daily activities
      deleteKeyboards?: boolean;     // User keyboards
      deleteProfile?: boolean;       // User profile and preferences
    }
  ): Promise<{ deletedCollections: string[]; deletedCounts: Record<string, number> }> {
    try {
      console.log(`🗑️ HARD DELETE: Starting selective deletion for user ${userId}`, options);
      
      const deletedCollections: string[] = [];
      const deletedCounts: Record<string, number> = {};

      // Delete progress data (training sessions, aggregations, level completions, daily activities)
      if (options.deleteProgress) {
        const progressSubCollections = ['training_sessions', 'aggregations', 'levelCompletions', 'dailyActivities'];
        
        for (const subCollection of progressSubCollections) {
          const subCollectionRef = collection(db, this.USERS_COLLECTION, userId, subCollection);
          const snapshot = await getDocs(subCollectionRef);
          console.log(`  🗑️ Deleting ${snapshot.size} documents from ${subCollection}`);
          
          const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
          await Promise.all(deletePromises);
          
          deletedCollections.push(subCollection);
          deletedCounts[subCollection] = snapshot.size;
        }

        // Delete user progress (root collection)
        const progressQuery = query(
          collection(db, this.PROGRESS_COLLECTION),
          where('userId', '==', userId)
        );
        const progressSnapshot = await getDocs(progressQuery);
        console.log(`  🗑️ Deleting ${progressSnapshot.size} documents from userProgress`);
        
        const progressDeletePromises = progressSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(progressDeletePromises);
        
        deletedCollections.push('userProgress');
        deletedCounts['userProgress'] = progressSnapshot.size;

        // Delete level history (root collection)
        const historyQuery = query(
          collection(db, this.LEVEL_HISTORY_COLLECTION),
          where('userId', '==', userId)
        );
        const historySnapshot = await getDocs(historyQuery);
        console.log(`  🗑️ Deleting ${historySnapshot.size} documents from levelHistory`);
        
        const historyDeletePromises = historySnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(historyDeletePromises);
        
        deletedCollections.push('levelHistory');
        deletedCounts['levelHistory'] = historySnapshot.size;
      }

      // Delete keyboards
      if (options.deleteKeyboards) {
        const keyboardsRef = collection(db, this.USERS_COLLECTION, userId, 'keyboards');
        const snapshot = await getDocs(keyboardsRef);
        console.log(`  🗑️ Deleting ${snapshot.size} documents from keyboards`);
        
        const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
        
        deletedCollections.push('keyboards');
        deletedCounts['keyboards'] = snapshot.size;
      }

      // Delete user profile and preferences
      if (options.deleteProfile) {
        await deleteDoc(doc(db, this.USERS_COLLECTION, userId));
        console.log(`  ✅ Deleted user profile document`);
        
        deletedCollections.push('userProfile');
        deletedCounts['userProfile'] = 1;
      }

      console.log(`✅ SELECTIVE DELETE COMPLETE for user ${userId}:`, deletedCounts);
      
      // CRITICAL: Clear localStorage as well to prevent data from being rebuilt
      // This must happen BEFORE rebuildAllAggregations is called
      if (options.deleteProgress || options.deleteKeyboards || options.deleteProfile) {
        console.log(`🧹 Clearing localStorage for user ${userId}...`);
        try {
          const { clearUserDataFromLocalStorage } = await import('../../utils/dataSyncUtils');
          clearUserDataFromLocalStorage(userId, {
            clearProgress: options.deleteProgress,
            clearKeyboards: options.deleteKeyboards,
            clearProfile: options.deleteProfile,
          });
          console.log(`✅ localStorage cleared for user ${userId}`);
        } catch (localStorageError) {
          console.error(`⚠️ Error clearing localStorage for ${userId}:`, localStorageError);
        }
      }
      
      // If progress was deleted, trigger aggregation recalculation (will rebuild from empty state)
      if (options.deleteProgress) {
        console.log(`🔄 Triggering aggregation recalculation for user ${userId}`);
        try {
          // Import dynamically to avoid circular dependency
          const { aggregationService } = await import('../AggregationService');
          await aggregationService.rebuildAllAggregations(userId);
          console.log(`✅ Aggregations recalculated for user ${userId} (should be empty)`);
        } catch (aggError) {
          console.error(`⚠️ Error recalculating aggregations for ${userId}:`, aggError);
          // Don't throw - deletion was successful, aggregation rebuild is optional
        }
      }
      
      return { deletedCollections, deletedCounts };
    } catch (error) {
      console.error(`❌ Error deleting user data for ${userId}:`, error);
      throw error;
    }
  }

  // Global Configuration Operations
  /**
   * Get global configuration setting
   * Used for admin-configured settings that apply to ALL users
   */
  async getGlobalConfig<T>(key: string): Promise<T | null> {
    try {
      const docRef = doc(db, this.GLOBAL_CONFIG_COLLECTION, key);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log(`🌍 Loaded GLOBAL config from Firestore [${key}]:`, data.value);
        return data.value as T;
      }
      console.log(`ℹ️ No global config found in Firestore for key: ${key}`);
      return null;
    } catch (error) {
      console.error(`Error getting global config [${key}]:`, error);
      throw error;
    }
  }

  /**
   * Set global configuration setting
   * Used by admins to configure settings that apply to ALL users
   */
  async setGlobalConfig<T>(key: string, value: T): Promise<void> {
    try {
      const docRef = doc(db, this.GLOBAL_CONFIG_COLLECTION, key);
      await setDoc(docRef, {
        value,
        updatedAt: Timestamp.now(),
      });
      console.log(`🌍 Saved GLOBAL config to Firestore [${key}]:`, value);
    } catch (error) {
      console.error(`Error setting global config [${key}]:`, error);
      throw error;
    }
  }

  // Training Session Progress Operations (New Format)
  /**
   * Save training session progress (completedLevels, bestPerformances, etc.)
   */
  async saveTrainingSession(userId: string, language: string, keyboardId: string, sessionData: any): Promise<void> {
    try {
      const sessionId = `${language}_${keyboardId}`;
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'training_sessions', sessionId);
      
      await setDoc(docRef, {
        ...sessionData,
        language,
        keyboardId,
        lastUpdated: Timestamp.now(),
      }, { merge: true }); // Merge to avoid overwriting entire document
      
      console.log(`☁️ Saved training session to Firestore: ${sessionId}`);
    } catch (error) {
      console.error('Error saving training session:', error);
      throw error;
    }
  }

  /**
   * Get training session progress
   */
  async getTrainingSession(userId: string, language: string, keyboardId: string): Promise<any | null> {
    try {
      const sessionId = `${language}_${keyboardId}`;
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'training_sessions', sessionId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log(`☁️ Loaded training session from Firestore: ${sessionId}`);
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error getting training session:', error);
      throw error;
    }
  }

  /**
   * Get all training sessions for a user (excludes deleted sessions)
   */
  async getAllTrainingSessions(userId: string): Promise<any[]> {
    try {
      const sessionsRef = collection(db, this.USERS_COLLECTION, userId, 'training_sessions');
      const querySnapshot = await getDocs(sessionsRef);
      
      const sessions: any[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Only include non-deleted sessions
        if (!data.deleted) {
          sessions.push({ id: doc.id, ...data });
        }
      });
      
      console.log(`☁️ Loaded ${sessions.length} active training sessions from Firestore (filtered out deleted)`);
      return sessions;
    } catch (error) {
      console.error('Error getting all training sessions:', error);
      throw error;
    }
  }

  // Keyboard Operations
  /**
   * Save user keyboard
   */
  /**
   * Get all keyboards for a user from Firestore
   */
  async getUserKeyboards(userId: string): Promise<Keyboard[]> {
    try {
      const keyboardsRef = collection(db, this.USERS_COLLECTION, userId, 'keyboards');
      const snapshot = await getDocs(keyboardsRef);
      
      const keyboards: Keyboard[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        keyboards.push({
          ...data,
          id: doc.id,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Keyboard);
      });
      
      console.log(`☁️ Loaded ${keyboards.length} keyboards from Firestore for user ${userId}`);
      return keyboards;
    } catch (error) {
      console.error('Error getting user keyboards from Firestore:', error);
      return [];
    }
  }

  async saveKeyboard(userId: string, keyboard: Keyboard): Promise<void> {
    try {
      // Validate keyboard ID
      if (!keyboard.id || typeof keyboard.id !== 'string') {
        console.error('❌ Invalid keyboard ID:', keyboard.id);
        throw new Error('Invalid keyboard ID');
      }
      
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'keyboards', keyboard.id);
      
      // Helper to convert Date or string to Timestamp with validation
      const toTimestamp = (date: Date | string | undefined): any => {
        if (!date) {
          console.warn('⚠️ Missing date, using current timestamp');
          return Timestamp.now();
        }
        
        let dateObj: Date;
        
        if (date instanceof Date) {
          dateObj = date;
        } else {
          // Convert string to Date
          dateObj = new Date(date);
        }
        
        // Check if date is valid
        if (isNaN(dateObj.getTime())) {
          console.warn('⚠️ Invalid date detected, using current timestamp:', date);
          return Timestamp.now();
        }
        
        return Timestamp.fromDate(dateObj);
      };
      
      await setDoc(docRef, {
        ...keyboard,
        createdAt: toTimestamp(keyboard.createdAt),
        updatedAt: toTimestamp(keyboard.updatedAt),
      });
      
      console.log(`☁️ Saved keyboard to Firestore: ${keyboard.id}`);
    } catch (error) {
      console.error('Error saving keyboard:', error);
      throw error;
    }
  }

  /**
   * Get user keyboard
   */
  async getKeyboard(userId: string, keyboardId: string): Promise<Keyboard | null> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'keyboards', keyboardId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Keyboard;
      }
      return null;
    } catch (error) {
      console.error('Error getting keyboard:', error);
      throw error;
    }
  }

  /**
   * Get all user keyboards (excludes deleted keyboards)
   */
  async getAllKeyboards(userId: string): Promise<Keyboard[]> {
    try {
      const keyboardsRef = collection(db, this.USERS_COLLECTION, userId, 'keyboards');
      const querySnapshot = await getDocs(keyboardsRef);
      
      const keyboards: Keyboard[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Only include non-deleted keyboards
        if (!data.deleted) {
          keyboards.push({
            ...data,
            createdAt: data.createdAt.toDate(),
            updatedAt: data.updatedAt.toDate(),
          } as Keyboard);
        }
      });
      
      console.log(`☁️ Loaded ${keyboards.length} active keyboards from Firestore (filtered out deleted)`);
      return keyboards;
    } catch (error) {
      console.error('Error getting all keyboards:', error);
      throw error;
    }
  }

  /**
   * Mark keyboard as deleted (soft delete - preserves historical data)
   */
  async markKeyboardAsDeleted(userId: string, keyboardId: string): Promise<void> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'keyboards', keyboardId);
      await updateDoc(docRef, {
        deleted: true,
        deletedAt: Timestamp.now(),
      });
      console.log(`☁️ Marked keyboard as deleted in Firestore: ${keyboardId}`);
    } catch (error) {
      console.error('Error marking keyboard as deleted:', error);
      throw error;
    }
  }

  /**
   * Delete user keyboard (hard delete - USE WITH CAUTION)
   */
  async deleteKeyboard(userId: string, keyboardId: string): Promise<void> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'keyboards', keyboardId);
      await deleteDoc(docRef);
      console.log(`☁️ Deleted keyboard from Firestore: ${keyboardId}`);
    } catch (error) {
      console.error('Error deleting keyboard:', error);
      throw error;
    }
  }

  /**
   * Mark a training session as deleted (soft delete - preserves historical data)
   */
  async markTrainingSessionAsDeleted(userId: string, sessionId: string): Promise<void> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'training_sessions', sessionId);
      await updateDoc(docRef, {
        deleted: true,
        deletedAt: Timestamp.now(),
      });
      console.log(`☁️ Marked training session as deleted in Firestore: ${sessionId}`);
    } catch (error) {
      console.error('Error marking training session as deleted:', error);
      throw error;
    }
  }

  /**
   * Delete a specific training session (hard delete - USE WITH CAUTION)
   */
  async deleteTrainingSession(userId: string, sessionId: string): Promise<void> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'training_sessions', sessionId);
      await deleteDoc(docRef);
      console.log(`☁️ Deleted training session from Firestore: ${sessionId}`);
    } catch (error) {
      console.error('Error deleting training session:', error);
      throw error;
    }
  }

  /**
   * Mark all training sessions for a keyboard as deleted (soft delete)
   */
  async markTrainingSessionsAsDeletedByKeyboard(userId: string, keyboardId: string): Promise<void> {
    try {
      const sessionsRef = collection(db, this.USERS_COLLECTION, userId, 'training_sessions');
      const q = query(sessionsRef, where('keyboardId', '==', keyboardId));
      const querySnapshot = await getDocs(q);
      
      const updatePromises = querySnapshot.docs.map(docSnapshot => 
        updateDoc(docSnapshot.ref, {
          deleted: true,
          deletedAt: Timestamp.now(),
        })
      );
      await Promise.all(updatePromises);
      
      console.log(`☁️ Marked ${querySnapshot.docs.length} training sessions as deleted for keyboard ${keyboardId}`);
    } catch (error) {
      console.error('Error marking training sessions as deleted:', error);
      throw error;
    }
  }
  
  /**
   * Mark aggregation as deleted (soft delete)
   */
  async markAggregationAsDeleted(userId: string, aggId: string): Promise<void> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'aggregations', aggId);
      await updateDoc(docRef, {
        deleted: true,
        deletedAt: Timestamp.now(),
      });
      console.log(`☁️ Marked aggregation as deleted in Firestore: ${aggId}`);
    } catch (error: any) {
      // If the document doesn't exist in Firestore, that's fine - it may not have been synced yet
      // The aggregation is already marked as deleted in localStorage (which is the source of truth)
      if (error?.code === 'not-found' || error?.message?.includes('No document to update')) {
        console.log(`⚠️ Aggregation ${aggId} not found in Firestore (may not have been synced yet) - skipping Firestore deletion`);
        return; // Don't throw - this is expected for newly created aggregations
      }
      
      // For other errors, log and continue (don't throw - deletion should be best-effort)
      console.error('Error marking aggregation as deleted:', error);
      console.warn(`⚠️ Could not mark aggregation ${aggId} as deleted in Firestore (may not exist yet):`, error?.message || error);
    }
  }
  
  /**
   * Mark all aggregations for a user as deleted
   */
  async markAllAggregationsAsDeleted(userId: string): Promise<void> {
    try {
      const aggsRef = collection(db, this.USERS_COLLECTION, userId, 'aggregations');
      const querySnapshot = await getDocs(aggsRef);
      
      const updatePromises = querySnapshot.docs.map(docSnapshot => 
        updateDoc(docSnapshot.ref, {
          deleted: true,
          deletedAt: Timestamp.now(),
        })
      );
      await Promise.all(updatePromises);
      
      console.log(`☁️ Marked ${querySnapshot.docs.length} aggregations as deleted for user ${userId}`);
    } catch (error) {
      console.error('Error marking all aggregations as deleted:', error);
      throw error;
    }
  }

  /**
   * Delete all training sessions for a specific keyboard (hard delete - USE WITH CAUTION)
   */
  async deleteTrainingSessionsByKeyboard(userId: string, keyboardId: string): Promise<void> {
    try {
      const sessionsRef = collection(db, this.USERS_COLLECTION, userId, 'training_sessions');
      const q = query(sessionsRef, where('keyboardId', '==', keyboardId));
      const querySnapshot = await getDocs(q);
      
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      console.log(`☁️ Deleted ${querySnapshot.docs.length} training sessions for keyboard ${keyboardId}`);
    } catch (error) {
      console.error('Error deleting training sessions:', error);
      throw error;
    }
  }

  /**
   * Delete user progress document
   */
  async deleteUserProgress(userId: string, language: string, keyboardId: string): Promise<void> {
    try {
      const progressId = `${userId}_${language}_${keyboardId}`;
      const docRef = doc(db, this.PROGRESS_COLLECTION, progressId);
      await deleteDoc(docRef);
      console.log(`☁️ Deleted user progress from Firestore: ${progressId}`);
    } catch (error) {
      console.error('Error deleting user progress:', error);
      throw error;
    }
  }

  // Daily Activity Operations
  /**
   * Save daily activity for a user
   */
  async saveDailyActivity(userId: string, dailyActivity: DailyActivity): Promise<void> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'dailyActivities', dailyActivity.date);
      
      await setDoc(docRef, {
        ...dailyActivity,
        activities: dailyActivity.activities.map(activity => ({
          ...activity,
          timestamp: Timestamp.fromDate(activity.timestamp),
        })),
      });
      
      console.log(`☁️ Saved daily activity to Firestore: ${dailyActivity.date}`);
    } catch (error) {
      console.error('Error saving daily activity:', error);
      throw error;
    }
  }

  /**
   * Get ALL daily activities for a user from Firestore
   * Used for syncing data to a new browser/domain
   */
  async getAllDailyActivities(userId: string): Promise<DailyActivity[]> {
    try {
      const activitiesRef = collection(db, this.USERS_COLLECTION, userId, 'dailyActivities');
      const querySnapshot = await getDocs(activitiesRef);
      
      const activities: DailyActivity[] = [];
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        activities.push({
          date: docSnap.id,
          totalLevels: data.totalLevels || 0,
          modeBreakdown: data.modeBreakdown || [],
          activities: (data.activities || []).map((activity: any) => ({
            ...activity,
            timestamp: activity.timestamp?.toDate?.() || new Date(),
          })),
        });
      });
      
      // Sort by date (newest first)
      activities.sort((a, b) => b.date.localeCompare(a.date));
      
      console.log(`☁️ Retrieved ${activities.length} daily activities from Firestore for user ${userId}`);
      return activities;
    } catch (error) {
      console.error('Error getting all daily activities:', error);
      return [];
    }
  }

  /**
   * Get daily activity for a specific date
   */
  async getDailyActivity(userId: string, date: string): Promise<DailyActivity | null> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'dailyActivities', date);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          ...data,
          activities: data.activities.map((activity: any) => ({
            ...activity,
            timestamp: activity.timestamp.toDate(),
          })),
        } as DailyActivity;
      }
      return null;
    } catch (error) {
      console.error('Error getting daily activity:', error);
      throw error;
    }
  }

  /**
   * Load daily activities for a user within a date range
   */
  async loadDailyActivities(
    userId: string,
    startDate?: string,
    endDate?: string
  ): Promise<DailyActivity[]> {
    try {
      const activitiesRef = collection(db, this.USERS_COLLECTION, userId, 'dailyActivities');
      
      let q = query(activitiesRef, orderBy('date', 'desc'));
      
      if (startDate && endDate) {
        q = query(activitiesRef, where('date', '>=', startDate), where('date', '<=', endDate), orderBy('date', 'desc'));
      }
      
      const querySnapshot = await getDocs(q);
      const activities: DailyActivity[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        activities.push({
          ...data,
          activities: data.activities.map((activity: any) => ({
            ...activity,
            timestamp: activity.timestamp.toDate(),
          })),
        } as DailyActivity);
      });
      
      console.log(`☁️ Loaded ${activities.length} daily activities from Firestore`);
      return activities;
    } catch (error) {
      console.error('Error loading daily activities:', error);
      throw error;
    }
  }

  // Activity Aggregation Operations
  /**
   * Save activity aggregation
   */
  async saveActivityAggregation(userId: string, aggregation: ActivityAggregation): Promise<void> {
    try {
      const aggId = `${aggregation.type}_${aggregation.filterValue}`;
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'activityAggregations', aggId);
      
      // Filter out undefined values from dailyBreakdown (Firestore doesn't accept undefined)
      const cleanedAggregation = {
        ...aggregation,
        dailyBreakdown: aggregation.dailyBreakdown.map(day => {
          const cleanDay: any = {
            date: day.date,
            levelCount: day.levelCount,
            sessions: day.sessions,
          };
          if (day.avgWpm !== undefined) cleanDay.avgWpm = day.avgWpm;
          if (day.avgTpm !== undefined) cleanDay.avgTpm = day.avgTpm;
          if (day.avgAccuracy !== undefined) cleanDay.avgAccuracy = day.avgAccuracy;
          return cleanDay;
        }),
        lastUpdated: Timestamp.fromDate(aggregation.lastUpdated),
      };
      
      await setDoc(docRef, cleanedAggregation);
      
      console.log(`☁️ Saved activity aggregation to Firestore: ${aggId}`);
    } catch (error) {
      console.error('Error saving activity aggregation:', error);
      throw error;
    }
  }

  /**
   * Prepare aggregation data for Firestore (helper method)
   */
  private prepareAggregationForFirestore(aggregation: any): any {
    // Prepare data for Firestore (convert Date objects to Timestamps)
    const firestoreData: any = {
      ...aggregation,
      lastUpdated: aggregation.lastUpdated instanceof Date 
        ? Timestamp.fromDate(aggregation.lastUpdated) 
        : Timestamp.now(),
      localTimestamp: aggregation.localTimestamp instanceof Date
        ? Timestamp.fromDate(aggregation.localTimestamp)
        : Timestamp.now(),
    };
    
    // Clean up dailyBreakdown if present
    if (aggregation.dailyBreakdown) {
      firestoreData.dailyBreakdown = aggregation.dailyBreakdown.map((day: any) => {
        const cleanDay: any = { date: day.date, levelCount: day.levelCount };
        if (day.sessions !== undefined) cleanDay.sessions = day.sessions;
        if (day.avgWpm !== undefined) cleanDay.avgWpm = day.avgWpm;
        if (day.avgTpm !== undefined) cleanDay.avgTpm = day.avgTpm;
        if (day.avgAccuracy !== undefined) cleanDay.avgAccuracy = day.avgAccuracy;
        return cleanDay;
      });
    }
    
    return firestoreData;
  }

  /**
   * Save unified aggregation (NEW system - replaces Activity + KeyStats aggregations)
   * Stores aggregations from the new AggregationService
   */
  async saveAggregation(userId: string, aggregation: any): Promise<void> {
    try {
      const aggId = aggregation.id;
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'aggregations', aggId);
      const firestoreData = this.prepareAggregationForFirestore(aggregation);
      await setDoc(docRef, firestoreData, { merge: true });
      // console.log(`☁️ Synced aggregation to Firestore: ${aggId}`);
    } catch (error) {
      console.error('Error saving unified aggregation:', error);
      throw error;
    }
  }

  /**
   * Batch save multiple aggregations to Firestore
   * Uses Firestore batch writes for optimal performance
   */
  async batchSaveAggregations(
    userId: string, 
    aggregations: any[]
  ): Promise<void> {
    if (aggregations.length === 0) {
      return;
    }
    
    try {
      // writeBatch can blow the gRPC "Transaction too big" limit when docs
      // are fat (rich keyData/confusions arrays), and the threshold is hard
      // to predict from JSON size alone. Issue independent setDoc writes
      // with bounded concurrency — each is its own commit, no shared cap.
      const CONCURRENCY = 8;
      let cursor = 0;
      let synced = 0;

      const worker = async () => {
        while (cursor < aggregations.length) {
          const idx = cursor++;
          const agg = aggregations[idx];
          const docRef = doc(db, this.USERS_COLLECTION, userId, 'aggregations', agg.id);
          const data = this.prepareAggregationForFirestore(agg);
          await setDoc(docRef, data, { merge: true });
          synced++;
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, aggregations.length) }, worker)
      );

      console.log(`  ✅ Synced ${synced} aggregations (concurrent setDoc)`);
    } catch (error) {
      console.error('Error saving aggregations:', error);
      throw error;
    }
  }

  /**
   * Get ALL aggregations for a user from Firestore
   * Used for syncing data to a new browser/domain
   */
  async getAllAggregations(userId: string): Promise<any[]> {
    try {
      const aggregationsRef = collection(db, this.USERS_COLLECTION, userId, 'aggregations');
      const querySnapshot = await getDocs(aggregationsRef);
      
      const aggregations: any[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        aggregations.push({
          ...data,
          id: doc.id,
          lastUpdated: data.lastUpdated?.toDate?.() || new Date(),
          localTimestamp: data.localTimestamp?.toDate?.() || new Date(),
        });
      });
      
      console.log(`☁️ Retrieved ${aggregations.length} aggregations from Firestore for user ${userId}`);
      return aggregations;
    } catch (error) {
      console.error('Error getting all aggregations:', error);
      return [];
    }
  }

  /**
   * Save user points to Firestore
   */
  async saveUserPoints(userId: string, points: any): Promise<void> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'stats', 'points');
      await setDoc(docRef, {
        ...points,
        lastUpdated: Timestamp.now(),
      }, { merge: true });
      console.log(`☁️ Synced points to Firestore: ${points.total} total`);
    } catch (error: any) {
      // Handle permission errors gracefully
      if (error?.code === 'permission-denied') {
        console.warn('⚠️ Points save not available (Firestore rules need updating for stats/points path)');
        return;
      }
      console.error('Error saving user points:', error);
    }
  }

  /**
   * Get user points from Firestore
   */
  async getUserPoints(userId: string): Promise<any | null> {
    try {
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'stats', 'points');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log(`☁️ Retrieved points from Firestore: ${data.total} total`);
        return {
          total: data.total || 0,
          earned: data.earned || 0,
          spent: data.spent || 0,
          history: data.history || [],
        };
      }
      return null;
    } catch (error: any) {
      // Handle permission errors gracefully - security rules may not allow this path yet
      if (error?.code === 'permission-denied') {
        console.warn('⚠️ Points sync not available (Firestore rules need updating for stats/points path)');
        return null;
      }
      console.error('Error getting user points:', error);
      return null;
    }
  }

  /**
   * Get activity aggregation
   */
  async getActivityAggregation(
    userId: string,
    type: string,
    filterValue: string
  ): Promise<ActivityAggregation | null> {
    try {
      const aggId = `${type}_${filterValue}`;
      const docRef = doc(db, this.USERS_COLLECTION, userId, 'activityAggregations', aggId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          ...data,
          lastUpdated: data.lastUpdated.toDate(),
        } as ActivityAggregation;
      }
      return null;
    } catch (error) {
      console.error('Error getting activity aggregation:', error);
      throw error;
    }
  }

  /**
   * Save a per-language word stats doc.
   */
  async saveWordStats(userId: string, doc_: { id: string; language: string; lastUpdated: Date; localTimestamp: Date; words: Record<string, unknown> }): Promise<void> {
    const docRef = doc(db, this.USERS_COLLECTION, userId, 'wordStats', doc_.language);
    await setDoc(docRef, {
      ...doc_,
      lastUpdated: Timestamp.fromDate(doc_.lastUpdated),
      localTimestamp: Timestamp.fromDate(doc_.localTimestamp),
    });
  }

  /**
   * Read a per-language word stats doc. Returns null if absent.
   */
  async getWordStats(userId: string, language: string): Promise<{ id: string; language: 'english' | 'hebrew' | 'russian'; lastUpdated: Date; localTimestamp: Date; words: Record<string, any> } | null> {
    const docRef = doc(db, this.USERS_COLLECTION, userId, 'wordStats', language);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      id: data.id ?? `wordStats_${language}`,
      language: data.language ?? language,
      lastUpdated: data.lastUpdated?.toDate?.() ?? new Date(),
      localTimestamp: data.localTimestamp?.toDate?.() ?? new Date(),
      words: data.words ?? {},
    };
  }
}

export const firestoreService = new FirestoreService();

